<script>
import HeartIcon from "../Icons/HeartIcon.vue";
import CareIcon from "../Icons/CareIcon.vue";
import WalkerIcon from "../Icons/WalkerIcon.vue";
import PostCard from "../PostCard.vue";
import RightRoundedArrow from "../Icons/RoundedRightArrow.vue";
import Skeleton from "../Skeleton.vue";

export default {
  props: {
    overviewPagesData: {
      type: Array,
      required: false,
    },
  },
  emits: ["collapseMenu"],
  components: {
    HeartIcon,
    CareIcon,
    WalkerIcon,
    PostCard,
    RightRoundedArrow,
    Skeleton,
  },
  data() {
    return {
      currentPath: window.location.pathname.slice(1),
      focusedPortal: this.overviewPagesData?.length
        ? this.overviewPagesData[0].parentFolder.slug
        : null,
    };
  },
  computed: {
    iconsPerPortal() {
      return {
        gesundheit: HeartIcon,
        pflege: CareIcon,
        hilfsmittel: WalkerIcon,
        default: HeartIcon, // TODO: Find a default icon
      };
    },

    portalData() {
      let portal = this.focusedPortal;
      if (!portal && this.overviewPagesData?.length) {
        portal = this.overviewPagesData[0].parentFolder.slug;
      }
      if (!portal) return null;

      return this.overviewPagesData?.find(
        (page) => page.overviewStory.slug === portal
      );
    },

    showInHeaderPostChunks() {
      const posts = this.portalData?.showInHeaderPosts || [];

      // splitted into chunks of 3
      return posts.reduce((acc, curr, i) => {
        const chunkIndex = Math.floor(i / 3);
        if (!acc[chunkIndex]) {
          acc[chunkIndex] = [];
        }
        acc[chunkIndex].push(curr);
        return acc;
      }, []);
    },

    highlightedPosts() {
      return this.portalData?.highlightedPosts || [];
    },

    currentPortalSlug() {
      const page = this.overviewPagesData?.find((page) =>
        this.currentPath.startsWith(page.overviewStory.full_slug)
      );

      return page?.parentFolder.slug;
    },
  },
  methods: {
    onFocusPortal(portal) {
      if (portal === this.focusedPortal) return;
      this.focusedPortal = portal;
    },

    isPortalFocused(portal) {
      let _portal = this.focusedPortal;
      if (!_portal && this.overviewPagesData?.length) {
        _portal = this.overviewPagesData[0].parentFolder.slug;
      }

      return portal === _portal;
    },

    onMenuMouseOver(e) {
      // check if the relatedTarget is a child of the expanded content
      if (e.relatedTarget?.closest(".header-expanded-content")) {
        return;
      }

      // check if the relatedTarget is the trigger
      if (e.relatedTarget?.classList.contains("desktop-header-container")) {
        return;
      }

      this.$emit("collapseMenu");
    },

    onKeyPressed(e) {
      if (e.key === "Escape") {
        this.$emit("collapseMenu");
        window.removeEventListener("keydown", this.onKeyPressed);
      }
    },

    isExpandedContentBiggerThanViewport() {
      const viewportHeight = window.innerHeight;
      const expandedContentHeight = this.$refs.expandedContent?.offsetHeight;
      return expandedContentHeight > viewportHeight;
    },

    focusNextPortal() {
      const portalIndex = this.overviewPagesData.findIndex(
        (page) => page.parentFolder.slug === this.focusedPortal
      );
      if (portalIndex === -1) return;

      let nextPortalIndex = portalIndex + 1;
      if (nextPortalIndex >= this.overviewPagesData.length) {
        nextPortalIndex = 0;
      }

      this.$nextTick(() => {
        this.onFocusPortal(
          this.overviewPagesData[nextPortalIndex].parentFolder.slug
        );

        this.$refs.portalItem[nextPortalIndex]?.focus();
      });
    },

    focusPreviousPortal() {
      const portalIndex = this.overviewPagesData.findIndex(
        (page) => page.parentFolder.slug === this.focusedPortal
      );
      if (portalIndex === -1) return;

      let previousPortalIndex = portalIndex - 1;
      if (previousPortalIndex < 0) {
        previousPortalIndex = this.overviewPagesData.length - 1;
      }

      this.$nextTick(() => {
        this.onFocusPortal(
          this.overviewPagesData[previousPortalIndex].parentFolder.slug
        );

        this.$refs.portalItem[previousPortalIndex]?.focus();
      });
    },
  },
  created() {
    window.addEventListener("keydown", this.onKeyPressed);
  },
  beforeDestroy() {
    window.removeEventListener("keydown", this.onKeyPressed);
  },
  watch: {
    focusedPortal() {
      const headerWrapper = document.querySelector(".anni-header-v2");
      const astroWrapper = document.querySelector(".anni-header-astro-wrapper");

      if (!headerWrapper) return;

      this.$nextTick(() => {
        if (this.isExpandedContentBiggerThanViewport()) {
          headerWrapper.style.position = "relative";
          if (astroWrapper) astroWrapper.style.position = "relative";
        } else {
          headerWrapper.style.position = "sticky";
          if (astroWrapper) astroWrapper.style.position = "sticky";
        }
      });
    },
  },
};
</script>

<template>
  <div
    @mouseout="onMenuMouseOver"
    class="header-expanded-content"
    id="header-expanded-content"
    aria-labelledby="expanded-content-trigger"
    ref="expandedContent"
  >
    <div class="sidebar">
      <ul class="portal-list" v-if="!overviewPagesData">
        <li v-for="_ in 3" :key="_">
          <Skeleton
            :width="220"
            :height="51"
            class="skeleton-portal nav-item"
          />
        </li>
      </ul>

      <ul class="portal-list" v-else>
        <li
          v-for="page in overviewPagesData"
          :key="page.parentFolder.slug"
          @mouseover="onFocusPortal(page.parentFolder.slug)"
          @focusin="onFocusPortal(page.parentFolder.slug)"
          @keydown.arrow-down.prevent="focusNextPortal"
          @keydown.arrow-up.prevent="focusPreviousPortal"
        >
          <a
            class="nav-item"
            :class="isPortalFocused(page.parentFolder.slug) ? 'active' : ''"
            :aria-label="`Portalseite öffnen: '${page.parentFolder.name}'`"
            :href="`/${page.overviewStory.full_slug}`"
            ref="portalItem"
          >
            <i>
              <component
                :is="
                  iconsPerPortal[page.overviewStory.slug] ||
                  iconsPerPortal.default
                "
                :color="
                  isPortalFocused(page.parentFolder.slug) ? '#fff' : undefined
                "
              />
            </i>
            <span
              :style="{
                color: isPortalFocused(page.parentFolder.slug)
                  ? '#fff'
                  : undefined,
              }"
            >
              {{ page.parentFolder.name }}
            </span>
          </a>
        </li>
      </ul>
    </div>

    <div class="content">
      <div class="nav-section">
        <h2>Häufig gelesen</h2>

        <ul class="h-posts" v-if="!portalData">
          <li class="h-posts-item" v-for="_ in 2" :key="_">
            <Skeleton :width="440" :height="136" />
          </li>
        </ul>

        <ul class="h-posts" v-if="portalData && highlightedPosts.length > 0">
          <li
            v-for="post in highlightedPosts"
            :key="post.id"
            class="h-posts-item"
          >
            <PostCard
              :post="post"
              :aria-labelledby="`carousel-item-${post.id}__heading`"
              :id="`carousel-item-${post.id}`"
            />
          </li>
        </ul>
      </div>

      <div class="nav-section">
        <div class="rest-of-posts" v-if="!portalData">
          <div class="rest-of-posts-column">
            <Skeleton
              class="rest-of-posts-item"
              v-for="_ in 3"
              :key="_"
              :height="35"
            />
          </div>

          <div class="rest-of-posts-column">
            <Skeleton
              class="rest-of-posts-item"
              v-for="_ in 3"
              :key="_"
              :height="35"
            />
          </div>
        </div>

        <div v-else class="rest-of-posts">
          <div
            v-for="(column, i) in showInHeaderPostChunks"
            :key="i"
            class="rest-of-posts-column"
          >
            <div
              v-for="post in column"
              :key="post.id"
              class="rest-of-posts-item"
            >
              <a class="nav-item" :href="`/${post.full_slug}`">
                <span :class="post.full_slug === currentPath ? 'active' : ''">
                  {{ post.name }}
                </span>
              </a>
            </div>
          </div>
        </div>
      </div>

      <div class="nav-section">
        <Skeleton
          v-if="!portalData"
          :width="306"
          :height="57"
          class="action-link"
        />

        <a
          v-else
          :href="`/${portalData.overviewStory.full_slug}`"
          class="action-link"
        >
          <span>Alle Beiträge anzeigen</span>
          <i><RightRoundedArrow /> </i>
        </a>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.header-expanded-content {
  background-color: #fff;
  width: 100%;
  position: absolute;
  left: 0;
  z-index: 1000;
  overflow: auto;
  border-top: 1px solid #eeeeee;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  box-shadow: 0 10px 2px -2px rgba(0, 0, 0, 0.0705882353);
}

.sidebar {
  background-color: #eeeeee;
  height: auto;
  width: 30%;
  padding-left: 22px;
  padding-right: 42px;
  padding-top: 47px;
  padding-bottom: 27px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;

  .portal-list {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    width: 100%;
    width: -webkit-fill-available;
    padding: 0;
    gap: 4px;

    li {
      list-style-type: none;
      width: 100%;
      width: -webkit-fill-available;

      .nav-item {
        width: 100%;
        width: -webkit-fill-available;
        max-width: 220px;
        margin-left: auto;
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        align-items: center;
        padding: 12px 15px;
        gap: 15px;
        border-radius: 6px;
        background-color: transparent;
        border: none;
        cursor: pointer;

        &:is(.skeleton-portal) {
          background-color: rgba(0, 0, 0, 0.1);
        }

        i {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 24px;
          width: 24px;

          svg {
            width: 100%;
            height: auto;
          }
        }

        span {
          font-weight: 400;
          color: #000000;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        &.active {
          background-color: #35887e;
          span {
            font-weight: 500;
            color: #fff;
          }
        }
      }
    }
  }
}

.content {
  width: 100%;
  width: -webkit-fill-available;
  padding-left: 44px;
  padding-right: 44px;
  padding-top: 44px;
  padding-bottom: 44px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 30px;

  .nav-section {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    gap: 15px;

    h2 {
      width: 100%;
      font-weight: 500;
      font-size: 22px;
      line-height: 33px;
    }
  }

  .h-posts {
    padding: 0;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-auto-flow: row;
    grid-gap: 20px;
    align-self: flex-start;

    .h-posts-item {
      width: 100%;
      min-width: 320px;
      max-width: 440px;
      list-style-type: none;
    }
  }

  @media (width < 1000px) {
    .h-posts {
      grid-template-columns: repeat(1, 1fr);
      grid-auto-flow: row;
    }
  }

  .rest-of-posts {
    width: 100%;
    padding: 0;
    display: flex;
    flex-direction: row;
    gap: 20px;
    justify-content: flex-start;

    .rest-of-posts-column {
      width: 100%;
      max-width: 440px;
      display: flex;
      flex-direction: column;
      gap: 2px;

      .rest-of-posts-item {
        list-style-type: none;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        padding: 4px;

        a {
          text-decoration: none;
          font-weight: 400;
          color: #323232;
          transition: color 0.1s ease-in-out;
          max-width: inherit;

          &:hover {
            color: #35887e;
          }
        }
      }
    }
  }

  .action-link {
    align-self: flex-end;
    background-color: #f7f7f7;
    border-radius: 6px;
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 15px 30px;
    gap: 20px;
    cursor: pointer;
    text-decoration: none;
    transition: background-color 0.2s ease-in-out;

    span {
      font-weight: 500;
      color: #000000;
    }

    i {
      display: flex;
      align-items: center;
    }

    &:hover {
      background-color: #eeeeee;
    }
  }
}

.active:not(button) {
  color: #35887e;

  &:is(.nav-item span) {
    font-weight: 500;
  }
}
</style>
